import {
  DOODLE_BADGES,
  type DoodleBadgeCategory,
  type DoodleBadgeId,
} from "./badges.data";

/**
 * The Doodles badge LIBRARY — one place every screen picks and resolves a
 * badge from (founder, 2026-10-03: "add all the badges like a library so we
 * reuse it a lot"). The full set is `DOODLE_BADGES`: the generated 191 of
 * the redesign's badges plus 40 hand-drawn additions (24 ingredients, 16
 * dishes/sections, `badges.additions.data.ts`, 2026-10-27) — the generated part — 74 of them the "Restaurant" set, 60 of which the
 * founder added on 2026-10-07: service, payment, kitchen, dishes and drinks);
 * what is stored on a record is only the badge's id,
 * so a record never carries markup and the art can be regenerated freely.
 */

const IDS = new Set<string>(DOODLE_BADGES.map((badge) => badge.id));

/** Every badge id the library ships, in the library's own order. */
export const ALL_DOODLE_BADGE_IDS: readonly DoodleBadgeId[] = DOODLE_BADGES.map(
  (badge) => badge.id,
);

/**
 * Every badge id with one category's badges FIRST, the rest after in the
 * library's own order — what a picker's "All icons" grid shows, so the
 * restaurant dashboard opens on its own "Restaurant" set before the gym,
 * transport and office ones.
 */
export function doodleBadgeIdsWithCategoryFirst(
  category: DoodleBadgeCategory,
): readonly DoodleBadgeId[] {
  const first = DOODLE_BADGES.filter((badge) => badge.category === category);
  const rest = DOODLE_BADGES.filter((badge) => badge.category !== category);
  return [...first, ...rest].map((badge) => badge.id);
}

/** The restaurant dashboard's "All icons" order: the Restaurant set first. */
export const RESTAURANT_FIRST_DOODLE_BADGE_IDS: readonly DoodleBadgeId[] =
  doodleBadgeIdsWithCategoryFirst("Restaurant");

/** Is this stored string a badge the library can draw? Unknown ids fall back. */
export function isDoodleBadgeId(value: unknown): value is DoodleBadgeId {
  return typeof value === "string" && IDS.has(value);
}

/** The badge's English name, for an accessible label on a picker option. */
export function doodleBadgeName(id: DoodleBadgeId): string {
  return DOODLE_BADGES.find((badge) => badge.id === id)?.name ?? id;
}

/**
 * The picker for a menu CATEGORY — and a DISH, which reuses it: the redesign's
 * seventeen, in its order (the category drawer's "Change icon" grid), then the
 * dishes and drinks of the Restaurant set added on 2026-10-07. Its 'flower' is
 * a line icon, not a badge, in the prototype's own library, so it draws
 * nothing there and is not listed. The service and payment marks (table,
 * waiter, refund, tip jar…) name no dish, so they wait behind "All icons".
 */
export const CATEGORY_BADGE_CHOICES: readonly DoodleBadgeId[] = [
  "burger",
  "pizza",
  "noodles",
  "pot",
  "cutlery",
  "cloche",
  "chef",
  "takeaway",
  "mug",
  "wine",
  "icecream",
  "foodbag",
  "menu",
  "star",
  "heart",
  "servicebell",
  "bill",
  // The 2026-10-07 Restaurant set: mains, sides, bakery and sweets…
  "combo",
  "grill",
  "chicken",
  "steak",
  "kebab",
  "seafood",
  "shrimp",
  "sushi",
  "pasta",
  "rice",
  "soup",
  "salad",
  "veg",
  "spicy",
  "wrap",
  "hotdog",
  "taco",
  "falafel",
  "fries",
  "cheese",
  "breakfast",
  "bread",
  "croissant",
  "pancakes",
  "waffle",
  "cake",
  "donut",
  "cookie",
  // …then the drinks.
  "espresso",
  "latte",
  "turkish-coffee",
  "iced-coffee",
  "tea",
  "iced-tea",
  "hot-chocolate",
  "juice",
  "smoothie",
  "lemonade",
  "milkshake",
  "mocktail",
  "soda",
  "sparkling",
  "addon",
  "discount",
  // Hand-drawn dishes and sections (badges.additions.data.ts, 2026-10-27).
  "sandwich",
  "toast",
  "pita-sandwich",
  "mozzarella-sticks",
  "fried-chicken",
  "calamari",
  "grilled-plate",
  "cordon-bleu",
  "stew",
  "appetizers",
  "main-course",
  "energy-drink",
  "water-bottle",
  "ful",
  "crepe",
  "dips",
];

/**
 * The picker for an INGREDIENT: the kitchen-store marks the redesign's stock
 * list draws (a package, a bottle, a jar, a crate…) first, then the food ones,
 * then the Restaurant set's stock marks and raw goods (2026-10-07).
 */
export const INGREDIENT_BADGE_CHOICES: readonly DoodleBadgeId[] = [
  // Hand-drawn ingredient marks (badges.additions.data.ts, 2026-10-27) first:
  // the raw goods a stock list is mostly made of.
  "milk",
  "cream",
  "oil",
  "flour",
  "egg",
  "salt",
  "sugar",
  "spices",
  "sauce",
  "mayo",
  "mushroom",
  "olives",
  "butter",
  "onion",
  "garlic",
  "tuna-can",
  "sausage",
  "honey",
  "chocolate",
  "coffee-beans",
  "nuts",
  "strawberry",
  "banana",
  "herbs",
  "package",
  "bottle",
  "protein",
  "mug",
  "wine",
  "icecream",
  "noodles",
  "pot",
  "foodbag",
  "takeaway",
  "burger",
  "pizza",
  "cloche",
  "chef",
  "cutlery",
  "warehouse",
  "scale",
  "heart",
  "star",
  "crate",
  "fridge",
  "prep",
  "cheese",
  "bread",
  "rice",
  "veg",
  "chicken",
  "steak",
  "seafood",
  "shrimp",
  "spicy",
  "pasta",
  "juice",
  "soda",
  "sparkling",
  "tea",
  "espresso",
];

/**
 * The first of `candidates` the library can draw, else `null` — the caller's
 * own default (the plate). A badge fallback is a chain (dish's own → its
 * section's), and an id this build no longer draws must fall through to the
 * next link, not stop the chain.
 */
export function firstDrawableBadge(
  candidates: ReadonlyArray<string | null | undefined>,
): DoodleBadgeId | null {
  for (const candidate of candidates) {
    if (isDoodleBadgeId(candidate)) return candidate;
  }
  return null;
}
